import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Bell, 
  Shield, 
  Database, 
  Smartphone, 
  Download,
  Trash2,
  LogOut,
  HelpCircle,
  RefreshCw,
  Wifi,
  Clock
} from 'lucide-react';
import { useAuth } from '../contexts';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { toast } from 'react-hot-toast';

const SettingsContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const Header = styled.header`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const HeaderContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const HeaderSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Main = styled.main`
  max-width: 800px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const SettingsSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[4]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const SettingDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Toggle = styled.button<{ active: boolean }>`
  position: relative;
  width: 48px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};
  
  ${({ active, theme }) => active ? `
    background: ${theme.colors.primary};
  ` : `
    background: ${theme.colors.gray300};
  `}

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ active }) => active ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: ${({ theme }) => theme.transition.fast};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  ${({ variant, theme }) => {
    switch (variant) {
      case 'danger':
        return `
          background: ${theme.colors.error};
          color: ${theme.colors.white};
          &:hover { background: ${theme.colors.errorDark}; }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.gray200};
          color: ${theme.colors.gray800};
          &:hover { background: ${theme.colors.gray300}; }
        `;
      default:
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.white};
          &:hover { background: ${theme.colors.primaryDark}; }
        `;
    }
  }}
`;

const StorageInfo = styled.div`
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  margin: ${({ theme }) => theme.spacing[4]} 0;
`;

const StorageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const StorageLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StorageValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
`;

interface SyncSettings {
  autoSync: boolean;
  syncInterval: number;
  syncOnStartup: boolean;
  syncOnNetworkChange: boolean;
  offlineMode: boolean;
}

const SettingsPage: React.FC = () => {
  const { logout } = useAuth();
  const [settings, setSettings] = useState({
    notifications: true,
    offlineMode: true,
    autoSync: true,
    darkMode: false,
    vibration: true,
    soundEnabled: true,
    sync: {
      autoSync: true,
      syncInterval: 5,
      syncOnStartup: true,
      syncOnNetworkChange: true,
      offlineMode: false
    } as SyncSettings
  });
  const [loading, setLoading] = useState(false);

  const toggleSetting = (section: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: value
    }));
    
    // Save to localStorage
    const savedSettings = {
      ...JSON.parse(localStorage.getItem('app_settings') || '{}'),
      [section]: value
    };
    localStorage.setItem('app_settings', JSON.stringify(savedSettings));
    
    // If sync settings changed, update sync service
    if (section === 'sync') {
      syncService.updateSettings(value);
    }
  };

  const handleClearCache = async () => {
    setLoading(true);
    try {
      await cacheService.clearAllCache();
      toast.success('Cache cleared successfully');
    } catch (error) {
      console.error('Failed to clear cache:', error);
      toast.error('Failed to clear cache');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      // Export data logic here
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Trigger download
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <SettingsContainer>
      <Header>
        <HeaderContent>
          <HeaderTitle>Settings</HeaderTitle>
          <HeaderSubtitle>Manage your app preferences and account settings</HeaderSubtitle>
        </HeaderContent>
      </Header>

      <Main>
        {/* Notifications */}
        <SettingsSection>
          <SectionHeader>
            <SectionIcon>
              <Bell size={20} />
            </SectionIcon>
            <SectionTitle>Notifications</SectionTitle>
          </SectionHeader>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Push Notifications</SettingLabel>
              <SettingDescription>Receive notifications about project updates and carton status changes</SettingDescription>
            </SettingInfo>
            <Toggle 
              active={settings.notifications} 
              onClick={() => toggleSetting('notifications', !settings.notifications)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Sound Effects</SettingLabel>
              <SettingDescription>Play sounds for scan confirmations and notifications</SettingDescription>
            </SettingInfo>
            <Toggle 
              active={settings.soundEnabled} 
              onClick={() => toggleSetting('soundEnabled', !settings.soundEnabled)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Vibration</SettingLabel>
              <SettingDescription>Vibrate on successful scans and important notifications</SettingDescription>
            </SettingInfo>
            <Toggle 
              active={settings.vibration} 
              onClick={() => toggleSetting('vibration', !settings.vibration)}
            />
          </SettingItem>
        </SettingsSection>

        {/* Data & Sync */}
        <SettingsSection>
          <SectionHeader>
            <SectionIcon>
              <Database size={20} />
            </SectionIcon>
            <SectionTitle>Data & Synchronization</SectionTitle>
          </SectionHeader>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Automatic Sync</SettingLabel>
              <SettingDescription>Automatically sync data in the background</SettingDescription>
            </SettingInfo>
            <Toggle
              active={settings.sync.autoSync}
              onClick={() => toggleSetting('sync', { ...settings.sync, autoSync: !settings.sync.autoSync })}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Sync Interval</SettingLabel>
              <SettingDescription>How often to sync data</SettingDescription>
            </SettingInfo>
            <select
              value={settings.sync.syncInterval}
              onChange={(e) => toggleSetting('sync', { ...settings.sync, syncInterval: Number(e.target.value) })}
              disabled={!settings.sync.autoSync}
              style={{
                padding: '0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e2e8f0'
              }}
            >
              <option value="1">1 minute</option>
              <option value="5">5 minutes</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
            </select>
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Sync on Startup</SettingLabel>
              <SettingDescription>Sync data when the app starts</SettingDescription>
            </SettingInfo>
            <Toggle
              active={settings.sync.syncOnStartup}
              onClick={() => toggleSetting('sync', { ...settings.sync, syncOnStartup: !settings.sync.syncOnStartup })}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Sync on Network Change</SettingLabel>
              <SettingDescription>Sync when network connection changes</SettingDescription>
            </SettingInfo>
            <Toggle
              active={settings.sync.syncOnNetworkChange}
              onClick={() => toggleSetting('sync', { ...settings.sync, syncOnNetworkChange: !settings.sync.syncOnNetworkChange })}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Offline Mode</SettingLabel>
              <SettingDescription>Work offline and sync when online</SettingDescription>
            </SettingInfo>
            <Toggle
              active={settings.sync.offlineMode}
              onClick={() => toggleSetting('sync', { ...settings.sync, offlineMode: !settings.sync.offlineMode })}
            />
          </SettingItem>

          <StorageInfo>
            <StorageRow>
              <StorageLabel>Cached Projects</StorageLabel>
              <StorageValue>12 items</StorageValue>
            </StorageRow>
            <StorageRow>
              <StorageLabel>Cached Cartons</StorageLabel>
              <StorageValue>245 items</StorageValue>
            </StorageRow>
            <StorageRow>
              <StorageLabel>Pending Sync</StorageLabel>
              <StorageValue>3 items</StorageValue>
            </StorageRow>
            <StorageRow>
              <StorageLabel>Storage Used</StorageLabel>
              <StorageValue>15.2 MB</StorageValue>
            </StorageRow>
          </StorageInfo>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Clear Cache</SettingLabel>
              <SettingDescription>Remove all cached data to free up storage space</SettingDescription>
            </SettingInfo>
            <ActionButton 
              variant="secondary" 
              onClick={handleClearCache}
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" /> : <Trash2 size={16} />}
              Clear Cache
            </ActionButton>
          </SettingItem>
        </SettingsSection>

        {/* Security */}
        <SettingsSection>
          <SectionHeader>
            <SectionIcon>
              <Shield size={20} />
            </SectionIcon>
            <SectionTitle>Security & Privacy</SectionTitle>
          </SectionHeader>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Export Data</SettingLabel>
              <SettingDescription>Download your data in JSON format for backup purposes</SettingDescription>
            </SettingInfo>
            <ActionButton onClick={handleExportData} disabled={loading}>
              {loading ? <LoadingSpinner size="sm" /> : <Download size={16} />}
              Export
            </ActionButton>
          </SettingItem>
        </SettingsSection>

        {/* App Preferences */}
        <SettingsSection>
          <SectionHeader>
            <SectionIcon>
              <Smartphone size={20} />
            </SectionIcon>
            <SectionTitle>App Preferences</SectionTitle>
          </SectionHeader>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>Dark Mode</SettingLabel>
              <SettingDescription>Switch to dark theme for better viewing in low light</SettingDescription>
            </SettingInfo>
            <Toggle 
              active={settings.darkMode} 
              onClick={() => toggleSetting('darkMode', !settings.darkMode)}
            />
          </SettingItem>

          <SettingItem>
            <SettingInfo>
              <SettingLabel>App Version</SettingLabel>
              <SettingDescription>Current version: 1.0.0</SettingDescription>
            </SettingInfo>
            <ActionButton variant="secondary">
              <HelpCircle size={16} />
              Help
            </ActionButton>
          </SettingItem>
        </SettingsSection>

        {/* Account Actions */}
        <SettingsSection>
          <SettingItem>
            <SettingInfo>
              <SettingLabel>Sign Out</SettingLabel>
              <SettingDescription>Sign out of your account and return to login screen</SettingDescription>
            </SettingInfo>
            <ActionButton variant="danger" onClick={handleLogout}>
              <LogOut size={16} />
              Sign Out
            </ActionButton>
          </SettingItem>
        </SettingsSection>
      </Main>
    </SettingsContainer>
  );
};

export default SettingsPage; 